import { GRID_SIZE } from "../constants/editor";
import { composeCardGrid } from "./card-scene";
import { getFrameColor, getFrameWeightPx } from "./card";
import type { CardDraft } from "../shared/game";

const EXPORT_WIDTH = 900;
const EXPORT_HEIGHT = 1320;
const OUTER_PADDING = 40;
const INNER_PADDING = 28;

export async function downloadCardJpeg(card: CardDraft): Promise<void> {
  const canvas = document.createElement("canvas");
  canvas.width = EXPORT_WIDTH;
  canvas.height = EXPORT_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }

  const frameColor = getFrameColor(card);
  const typeColor = getFrameColor({ ...card, frameColorManual: null });
  const borderWidth = getFrameWeightPx(card) * 3;

  drawCardShell(context, frameColor, borderWidth);
  drawHeader(context, card, typeColor);
  drawArt(context, card, typeColor);
  drawDescription(context, card, typeColor);
  drawFooter(context, card, typeColor);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.94));
  if (!blob) {
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${slugify(card.name || "bgj-card")}.jpg`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function drawCardShell(context: CanvasRenderingContext2D, frameColor: string, borderWidth: number): void {
  context.clearRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

  const background = context.createLinearGradient(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
  background.addColorStop(0, "#14102a");
  background.addColorStop(0.55, "#09070f");
  background.addColorStop(1, "#0c1120");

  roundedRect(context, OUTER_PADDING, OUTER_PADDING, EXPORT_WIDTH - OUTER_PADDING * 2, EXPORT_HEIGHT - OUTER_PADDING * 2, 44);
  context.fillStyle = background;
  context.fill();

  context.lineWidth = borderWidth;
  context.strokeStyle = frameColor;
  context.stroke();

  const sheen = context.createLinearGradient(OUTER_PADDING, OUTER_PADDING, EXPORT_WIDTH - OUTER_PADDING, EXPORT_HEIGHT - OUTER_PADDING);
  sheen.addColorStop(0, `${frameColor}10`);
  sheen.addColorStop(0.4, "rgba(255,255,255,0)");
  sheen.addColorStop(0.6, `${frameColor}0d`);
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  roundedRect(context, OUTER_PADDING + 4, OUTER_PADDING + 4, EXPORT_WIDTH - OUTER_PADDING * 2 - 8, EXPORT_HEIGHT - OUTER_PADDING * 2 - 8, 40);
  context.fillStyle = sheen;
  context.fill();
}

function drawHeader(context: CanvasRenderingContext2D, card: CardDraft, typeColor: string): void {
  const x = OUTER_PADDING + INNER_PADDING;
  const y = OUTER_PADDING + INNER_PADDING;
  const width = EXPORT_WIDTH - (OUTER_PADDING + INNER_PADDING) * 2;

  roundedRect(context, x, y, width * 0.72, 72, 18);
  context.fillStyle = "rgba(255,255,255,0.04)";
  context.fill();
  context.strokeStyle = "rgba(255,255,255,0.12)";
  context.lineWidth = 2;
  context.stroke();

  context.fillStyle = "#ffffff";
  context.font = "700 38px 'Space Grotesk', sans-serif";
  context.textBaseline = "middle";
  context.fillText(card.name || "My Monster", x + 20, y + 38);

  context.beginPath();
  context.arc(x + width - 150, y + 36, 7, 0, Math.PI * 2);
  context.fillStyle = typeColor;
  context.fill();

  roundedRect(context, x + width - 128, y + 14, 104, 44, 14);
  context.fillStyle = `${typeColor}22`;
  context.fill();
  context.strokeStyle = `${typeColor}`;
  context.lineWidth = 2;
  context.stroke();

  context.fillStyle = typeColor;
  context.font = "700 24px 'Space Grotesk', sans-serif";
  context.fillText(`HP ${card.hp}`, x + width - 112, y + 37);
}

function drawArt(context: CanvasRenderingContext2D, card: CardDraft, typeColor: string): void {
  const x = OUTER_PADDING + INNER_PADDING;
  const y = OUTER_PADDING + INNER_PADDING + 96;
  const size = EXPORT_WIDTH - (OUTER_PADDING + INNER_PADDING) * 2;
  const cellSize = Math.floor(size / GRID_SIZE);
  const artSize = cellSize * GRID_SIZE;
  const grid = composeCardGrid(card);

  roundedRect(context, x, y, artSize, artSize, 22);
  context.fillStyle = "#111322";
  context.fill();
  context.strokeStyle = `${typeColor}55`;
  context.lineWidth = 3;
  context.stroke();

  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      context.fillStyle = grid[row][col] ?? ((row + col) % 2 === 0 ? "#0d0b18" : "#151223");
      context.fillRect(x + col * cellSize, y + row * cellSize, cellSize, cellSize);
    }
  }
}

function drawDescription(context: CanvasRenderingContext2D, card: CardDraft, typeColor: string): void {
  const boxX = OUTER_PADDING + INNER_PADDING;
  const boxY = EXPORT_WIDTH + 118;
  const boxWidth = EXPORT_WIDTH - (OUTER_PADDING + INNER_PADDING) * 2;
  const boxHeight = 190;

  context.fillStyle = `${typeColor}88`;
  context.font = "12px 'Press Start 2P', monospace";
  context.textAlign = "center";
  context.fillText("DESCRIPTION", EXPORT_WIDTH / 2, boxY - 14);
  context.textAlign = "left";

  roundedRect(context, boxX, boxY, boxWidth, boxHeight, 18);
  context.fillStyle = "rgba(0,0,0,0.22)";
  context.fill();
  context.strokeStyle = "rgba(255,255,255,0.08)";
  context.lineWidth = 2;
  context.stroke();

  context.fillStyle = "#c3cad8";
  context.font = "24px 'Space Grotesk', sans-serif";
  wrapText(context, card.description || "Describe your monster's powers, origin, or special abilities.", boxX + 22, boxY + 36, boxWidth - 44, 34);
}

function drawFooter(context: CanvasRenderingContext2D, card: CardDraft, typeColor: string): void {
  const x = OUTER_PADDING + INNER_PADDING;
  const y = EXPORT_HEIGHT - OUTER_PADDING - INNER_PADDING - 58;
  roundedRect(context, x, y, 144, 40, 14);
  context.fillStyle = `${typeColor}1e`;
  context.fill();
  context.strokeStyle = `${typeColor}`;
  context.lineWidth = 2;
  context.stroke();

  context.fillStyle = typeColor;
  context.font = "700 22px 'Space Grotesk', sans-serif";
  context.textBaseline = "middle";
  context.fillText(card.typeLabel, x + 16, y + 22);

  const dotY = y + 20;
  const startX = EXPORT_WIDTH - OUTER_PADDING - INNER_PADDING - 76;
  for (let index = 0; index < 5; index += 1) {
    const radius = index === 2 ? 7 : 4;
    context.beginPath();
    context.arc(startX + index * 16, dotY, radius, 0, Math.PI * 2);
    context.fillStyle = index === 2 ? typeColor : `${typeColor}66`;
    context.fill();
  }
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(/\s+/);
  let line = "";
  let drawY = y;

  for (const word of words) {
    const nextLine = line ? `${line} ${word}` : word;
    if (context.measureText(nextLine).width > maxWidth && line) {
      context.fillText(line, x, drawY);
      line = word;
      drawY += lineHeight;
      continue;
    }
    line = nextLine;
  }

  if (line) {
    context.fillText(line, x, drawY);
  }
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
